import { AppDialog } from "../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../common/components/button/defaultButton/DefaultButton";
import { NotificationDialogAudience, NotificationDialogView } from "../../common/enums";
import { IosInstallSteps } from "./IosInstallSteps";
import { NewReservationsSteps } from "./NewReservationsSteps";
import { useNotificationPermissionDialog } from "./hooks/useNotificationPermissionDialog";
import styles from "./NotificationPermissionDialog.module.scss";

interface INotificationPermissionDialogProps {
  /** Clients on the reservation-success screens (default), or staff on opening the platform. */
  audience?: NotificationDialogAudience;
  /** Client audience: only confirmed reservations get the invitation (not the waiting list, not unpaid ones). */
  isConfirmedReservation?: boolean;
  studioName?: string | null;
  eventDate?: Date | string | null;
  startTime?: string | null;
}

/**
 * Asks whether the user wants notifications: clients on the reservation-success screens,
 * staff when they open the platform, with the way to the new-reservation switch in
 * "Моят профил". Hidden unless the enable-app-notifications flag is on
 * (see useNotificationPermissionDialog).
 */
export const NotificationPermissionDialog = ({
  audience,
  isConfirmedReservation,
  studioName,
  eventDate,
  startTime,
}: INotificationPermissionDialogProps) => {
  const {
    isVisible,
    view,
    isBusy,
    onEnable,
    onDismiss,
    subtitle,
    showPreview,
    showNewReservationsSteps,
    previewTitle,
    previewBody,
  } = useNotificationPermissionDialog({
    audience,
    isConfirmedReservation,
    studioName,
    eventDate,
    startTime,
  });

  return (
    // The common dialog opens in the page body: rendered inline, the booking wizards'
    // transformed container clipped a fixed backdrop to its own box. Answered only with
    // the buttons, as before (no header, no close button, no closing on the backdrop).
    <AppDialog
      visible={isVisible}
      onHide={onDismiss}
      showHeader={false}
      closable={false}
      className={styles.dialog}
      contentClassName={styles.content}
      ariaLabelledBy="notification-dialog-title"
    >
      {view === NotificationDialogView.Invite && (
        <div className={styles.view}>
          <div className={styles.hero}>
            <svg className={styles.bell} viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6v-5a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-1.7 1.7A1 1 0 0 0 4 19.4h16a1 1 0 0 0 .7-1.7L19 16Z" />
            </svg>
          </div>
          <h2 className={styles.title} id="notification-dialog-title">
            Да ви напомним ли?
          </h2>
          <p className={styles.subtitle}>{subtitle}</p>

          {showPreview && (
            <>
              <span className={styles.previewLabel}>Ще изглеждат така</span>
              <div className={styles.preview} aria-hidden="true">
                <img className={styles.previewIcon} src="/icon-192.png" alt="" />
                <div className={styles.previewText}>
                  <div className={styles.previewHead}>
                    <b>{previewTitle}</b>
                    <span>сега</span>
                  </div>
                  <div className={styles.previewBody}>{previewBody}</div>
                </div>
              </div>
            </>
          )}

          {showNewReservationsSteps && (
            <>
              <p className={styles.stepsIntro}>
                Известия за нови резервации във вашите часове се включват от профила ви:
              </p>
              <NewReservationsSteps />
            </>
          )}

          <div className={styles.actions}>
            <DefaultButton
              label="Включи известията"
              width="100%"
              loading={isBusy}
              disabled={isBusy}
              onClick={onEnable}
            />
            <DefaultButton
              label="Не сега"
              design="outline"
              width="100%"
              disabled={isBusy}
              onClick={onDismiss}
            />
          </div>
          <p className={styles.footnote}>Можете да ги изключите по всяко време от „Моят профил“.</p>
        </div>
      )}

      {view === NotificationDialogView.IosSteps && (
        <div className={styles.view}>
          <div className={styles.hero}>
            <svg className={styles.outlineIcon} viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="4" width="16" height="16" rx="4.5" />
              <path d="M12 8.5v7M8.5 12h7" />
            </svg>
          </div>
          <h2 className={styles.title} id="notification-dialog-title">
            Добавете Planny към началния екран
          </h2>
          <p className={styles.subtitle}>
            На iPhone известията работят, когато Planny е добавен на началния екран.
          </p>
          <IosInstallSteps />
          <div className={styles.actions}>
            <DefaultButton label="Разбрах" width="100%" onClick={onDismiss} />
          </div>
        </div>
      )}

      {view === NotificationDialogView.Done && (
        <div className={styles.view}>
          <div className={`${styles.hero} ${styles.heroDone}`}>
            <svg className={styles.outlineIcon} viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          </div>
          <h2 className={styles.title} id="notification-dialog-title">
            Готово!
          </h2>
          <p className={styles.subtitle}>Ще получавате известия на това устройство.</p>
        </div>
      )}
    </AppDialog>
  );
};
